import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found. Please log in again.');
      }
      console.log('Token for fetching notifications:', token);

      const response = await axios.get('https://backendumbracare.onrender.com/api/notifications', {
        headers: {
          'x-auth-token': token,
        },
      });
      setNotifications(response.data);
    } catch (err) {
      console.error('Error fetching notifications:', err.response?.data || err.message);
      setError(`Error fetching notifications: ${err.response?.data?.msg || err.message}`);
    }
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found. Please log in again.');
      }

      await axios.put(`https://backendumbracare.onrender.com/api/notifications/${id}/read`, {}, {
        headers: {
          'x-auth-token': token,
        },
      });
      
      setNotifications(notifications.map(notification => 
        notification._id === id ? { ...notification, isRead: true } : notification
      ));
      
      setSuccessMessage('Notification marked as read');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error marking notification as read:', err.response?.data || err.message);
      setError(`Error marking notification as read: ${err.response?.data?.msg || err.message}`);
    }
  };

  return (
    <motion.div 
      style={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        style={styles.notificationsContainer}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h2 style={styles.heading}>Notifications</h2>
        <AnimatePresence>
          {error && (
            <motion.p 
              style={styles.error}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.p>
          )}
          {successMessage && (
            <motion.p 
              style={styles.success}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {successMessage}
            </motion.p>
          )}
        </AnimatePresence>
        {notifications.length === 0 ? (
          <motion.p 
            style={styles.info}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            No notifications available.
          </motion.p>
        ) : (
          <div style={styles.notificationScrollContainer}>
            <ul style={styles.notificationList}>
              <AnimatePresence>
                {notifications.map((notification, index) => (
                  <motion.li
                    key={notification._id}
                    style={{
                      ...styles.notificationItem,
                      backgroundColor: notification.isRead ? '#f0f0f0' : '#f9f9f9',
                      borderLeft: notification.isRead ? '1px solid #ddd' : '3px solid #B15870'
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    exit={{ opacity: 0, x: 20 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <strong>{notification.type === 'medication_reminder' ? 'Medication Reminder' : 'Appointment Reminder'}:</strong> {notification.message}
                    <br />
                    <small>{new Date(notification.createdAt).toLocaleString()}</small>
                    <div style={styles.notificationActions}>
                      {!notification.isRead && (
                        <motion.button 
                          onClick={() => markAsRead(notification._id)} 
                          style={styles.markReadButton}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Mark as Read
                        </motion.button>
                      )}
                      {notification.isRead && (
                        <motion.span 
                          style={styles.readStatus}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          Read
                        </motion.span>
                      )}
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontFamily: "'Poppins', sans-serif",
    boxSizing: 'border-box',
    paddingTop: '90px',
    backgroundImage: 'url(/background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    zIndex: 1,
    '::before': { // Changed from separate overlay div to pseudo-element to match PregnancyPostpartumTracker
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(253, 232, 233, 0.7)', // Matches PregnancyPostpartumTracker
      zIndex: -1,
    }
  },
  notificationsContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(5px)',
    padding: '40px',
    borderRadius: '10px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    border: '2px solid #B15870',
    width: '100%',
    maxWidth: '600px',
    position: 'relative',
    zIndex: 2,
  },
  heading: {
    fontSize: '28px',
    color: '#B85170',
    marginBottom: '20px',
    fontWeight: '600',
  },
  info: {
    fontSize: '16px',
    color: '#000000',
  },
  error: {
    fontSize: '16px',
    color: '#FF4444',
    marginBottom: '20px',
  },
  success: {
    fontSize: '16px',
    backgroundColor: '#E7E5FF',
    color: '#3D348B',
    marginBottom: '20px',
    padding: '10px',
    borderRadius: '5px',
  },
  notificationScrollContainer: { // New style for scrollable container
    maxHeight: '450px', // Matches PregnancyPostpartumTracker and PeriodTracker
    overflowY: 'auto',
    textAlign: 'left',
  },
  notificationList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0, // Adjusted to fit within scroll container
  },
  notificationItem: {
    padding: '15px',
    marginBottom: '10px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    position: 'relative',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  notificationActions: {
    marginTop: '10px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  markReadButton: {
    backgroundColor: '#B15870',
    color: 'white',
    border: 'none',
    padding: '5px 10px',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'all 0.2s ease',
  },
  readStatus: {
    color: '#888',
    fontSize: '12px',
    fontStyle: 'italic',
  }
};

export default Notifications;